"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "motion/react";
import { useState } from "react";
import { NAV_ITEMS } from "@/lib/site";
import { useActiveSection } from "@/components/motion/use-active-section";
import { useI18n } from "@/i18n/dictionary-context";
import type { Dictionary } from "@/i18n/dictionaries";
import { pathForLocale, stripLocale } from "@/i18n/config";

const SECTION_IDS = NAV_ITEMS.filter((n) => n.kind === "anchor")
  .map((n) => n.sectionId!)
  .filter(Boolean);

const NAV_LABEL_KEYS: Record<string, keyof Dictionary["nav"]> = {
  Platform: "platform",
  "AI Agents": "aiAgents",
  Pricing: "pricing",
  "How it works": "howItWorks",
  FAQ: "faq",
  Blog: "blog",
};

export function DesktopNav() {
  const pathname = usePathname() ?? "/";
  const stripped = stripLocale(pathname);
  const onHome = stripped === "/" || stripped === "";
  const activeSection = useActiveSection(onHome ? SECTION_IDS : []);
  const { dict, locale } = useI18n();

  const [hoveredHref, setHoveredHref] = useState<string | null>(null);

  const isActive = (item: (typeof NAV_ITEMS)[number]): boolean => {
    if (item.kind === "page") {
      return stripped === item.href || stripped.startsWith(`${item.href}/`);
    }
    return onHome && activeSection === item.sectionId;
  };

  const focusedHref = hoveredHref ?? NAV_ITEMS.find(isActive)?.href ?? null;

  return (
    <nav
      className="hidden md:flex items-center"
      onMouseLeave={() => setHoveredHref(null)}
    >
      {NAV_ITEMS.map((item) => {
        const active = isActive(item);
        const focused = focusedHref === item.href;
        const labelKey = NAV_LABEL_KEYS[item.label];
        const label = labelKey ? dict.nav[labelKey] : item.label;
        return (
          <Link
            key={item.label}
            href={pathForLocale(item.href, locale)}
            onMouseEnter={() => setHoveredHref(item.href)}
            className="relative isolate rounded-lg px-3 py-2 text-[15px] font-medium transition-colors duration-200"
            aria-current={active ? "page" : undefined}
          >
            {focused && (
              <motion.span
                layoutId="nav-pill"
                aria-hidden
                className="absolute inset-0 -z-10 rounded-lg bg-brand-soft"
                transition={{ type: "spring", stiffness: 380, damping: 32, mass: 0.7 }}
              />
            )}
            <span
              className={
                active
                  ? "relative text-brand"
                  : focused
                  ? "relative text-neutral-950"
                  : "relative text-neutral-500 hover:text-neutral-950"
              }
            >
              {label}
            </span>
            {active && (
              <motion.span
                layoutId="nav-underline"
                aria-hidden
                className="absolute inset-x-3 -bottom-[1px] h-[2px] rounded-full bg-brand"
                transition={{ type: "spring", stiffness: 380, damping: 32, mass: 0.7 }}
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
}
